import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import type { PendingDose, DoseStatus } from '@rondela/shared'
import type { PetWithActions } from '../lib/urgency'
import type { SpeciesType } from '../../../shared/components/widgets/StatusCard'
import {
  createUnifiedTimeline,
  groupIntoBatches,
  splitImmediateAndLater,
} from '../lib/timeline'
import { getCardStatus, getNextActionPreview } from '../lib/card-status'
import { TimelineBatch } from './TimelineBatch'
import { LaterItemsChip } from './LaterItemsChip'
import { MissedDoseModal } from './MissedDoseModal'
import { RecentDoseActivity } from './RecentDoseActivity'
import { StatusCard, Avatar, Badge } from '../../../shared/components/widgets'

interface PetTaskCardProps {
  id: string
  petWithActions: PetWithActions
  onDoseAction: (doseId: string, status: DoseStatus, notes?: string) => Promise<void>
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

function getSpeciesEmoji(especie: string): string {
  return especie === 'perro' ? '🐕' : especie === 'gato' ? '🐈' : '🐾'
}

function getSpeciesType(especie: string): SpeciesType {
  if (especie === 'perro') return 'perro'
  if (especie === 'gato') return 'gato'
  return 'otro'
}

function getStatusType(cardStatus: string) {
  switch (cardStatus) {
    case 'overdue':
      return 'error' as const
    case 'urgent':
      return 'warning' as const
    default:
      return 'neutral' as const
  }
}

export function PetTaskCard({
  id,
  petWithActions,
  onDoseAction,
  expanded,
  onExpandedChange,
}: PetTaskCardProps) {
  const { pet, pendingDoses, recentDoses, upcomingAppointments, alerts } = petWithActions

  const [missedDose, setMissedDose] = useState<PendingDose | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Create unified timeline with doses and appointments
  const timelineItems = useMemo(
    () => createUnifiedTimeline(pendingDoses, upcomingAppointments),
    [pendingDoses, upcomingAppointments]
  )

  // Group into batches and split immediate vs later
  const { immediateBatches, laterItems } = useMemo(() => {
    const batches = groupIntoBatches(timelineItems)
    return splitImmediateAndLater(batches)
  }, [timelineItems])

  // Determine card status for background color
  const cardStatus = useMemo(() => getCardStatus(timelineItems), [timelineItems])
  const statusType = getStatusType(cardStatus)

  // Get preview text for header
  const nextActionPreview = useMemo(
    () => getNextActionPreview(timelineItems),
    [timelineItems]
  )

  const pendingCount = pendingDoses.filter(d => d.status === 'pending').length

  const handleTaken = async (dose: PendingDose) => {
    setIsLoading(true)
    try {
      await onDoseAction(dose.id, 'taken')
    } finally {
      setIsLoading(false)
    }
  }

  const handleMissedConfirm = async (instructions: string) => {
    if (!missedDose) return
    setIsLoading(true)
    try {
      await onDoseAction(missedDose.id, 'skipped', instructions)
      setMissedDose(null)
    } finally {
      setIsLoading(false)
    }
  }

  const hasContent =
    timelineItems.length > 0 || recentDoses.length > 0 || alerts.length > 0

  return (
    <>
      <StatusCard
        id={id}
        status={statusType}
        stripe
        stripeIcon={getSpeciesType(pet.especie)}
        expandable
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        className="pet-task-card"
      >
        <StatusCard.Header>
          <Link
            to={`/mascotas/${pet.id}`}
            onClick={(e) => e.stopPropagation()}
            className="pet-task-card__avatar-link"
          >
            <Avatar
              src={pet.fotoUrl}
              fallback={getSpeciesEmoji(pet.especie)}
              size="md"
              ring={statusType !== 'neutral'}
              ringColor={statusType}
            />
          </Link>
          <div className="pet-task-card__info">
            <StatusCard.Title>{pet.nombre}</StatusCard.Title>
            {nextActionPreview && (
              <StatusCard.Subtitle>{nextActionPreview}</StatusCard.Subtitle>
            )}
          </div>
          <div className="pet-task-card__meta">
            {pet.peso && <Badge variant="pill">{pet.peso} kg</Badge>}
            {pendingCount > 0 && (
              <Badge variant="counter" status="warning">
                {pendingCount}
              </Badge>
            )}
          </div>
        </StatusCard.Header>

        <StatusCard.Content>
          {/* Health Alerts */}
          {alerts.length > 0 && (
            <div className="pet-card-v2__alerts">
              {alerts.map((alert, idx) => (
                <div key={idx} className="pet-card-v2__alert">
                  <AlertCircle size={14} />
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Unified Timeline */}
          {timelineItems.length > 0 && (
            <div className="pet-card-v2__timeline">
              {/* Immediate Batches */}
              {immediateBatches.map((batch, index) => (
                <TimelineBatch
                  key={`batch-${batch.startTime.getTime()}-${index}`}
                  batch={batch}
                  onDoseTaken={handleTaken}
                  onDoseMissed={setMissedDose}
                  isLoading={isLoading}
                />
              ))}

              {/* Later Items Chip */}
              <LaterItemsChip
                items={laterItems}
                onDoseTaken={handleTaken}
                onDoseMissed={setMissedDose}
                isLoading={isLoading}
              />
            </div>
          )}

          {/* Recent Activity */}
          {recentDoses.length > 0 && (
            <div className="pet-card-v2__section">
              <RecentDoseActivity doses={recentDoses} />
            </div>
          )}

          {/* Empty State */}
          {!hasContent && (
            <p className="pet-card-v2__empty">Sin tareas pendientes</p>
          )}
        </StatusCard.Content>
      </StatusCard>

      {/* Missed Dose Modal */}
      {missedDose && (
        <MissedDoseModal
          medicationName={`${missedDose.medicationNombre} ${missedDose.dosis}`}
          petName={pet.nombre}
          onConfirm={handleMissedConfirm}
          onCancel={() => setMissedDose(null)}
          isLoading={isLoading}
        />
      )}
    </>
  )
}
